<template>
	<div>
		<div class="row mb-3">
			<div class="col-12">
				<h1><em class="fas fa-bus-alt"></em> {{config.year}}-{{config.year + 1}} School Year Bus Registration Form</h1>

				<p>Students attending their board-designated schools are eligible for distance-based transportation from their primary address as follows:</p>
				<ul>
					<li>Elementary aged students who reside greater than 1.6 kms from the school site</li>
					<li>Secondary aged students who reside greater than 3.2 kms from the school site</li>
				</ul>

				<div v-if="config.year == 2023">
					<p>If a residential address does not qualify for transportation based on distance as determined by STS, it is the responsibility of the parent/guardian to determine how the student will arrive at and depart from school.</p>
					<p>Students who require Specialized Transportation Services must apply for transportation using the Specialized Transportation Application found at <a href="http://www.mybigyellowbus.ca/our-forms" target="_blank">www.mybigyellowbus.ca/our-forms</a>.</p>
				</div>
				<div v-else>
					<p>If a residential address does not qualify for transportation based on distance as determined by STS, it is the responsibility of the parent/caregiver to determine how the student will arrive at and depart from school.</p>
				</div>
				<p>For transportation-eligible students, STS offers a scheduled, fixed route transportation model whereby eligible riders are assigned to both specific bus routes and bus stop locations.</p>
				<p>The service does not allow for flexible ridership for safety reasons and requests for assignments to other bus routes/stops for circumstances like part-time jobs, lessons, visitation at a friend's house etc. will be denied by the school, bus company and/or STS.</p>
				<p>A bus registration form must be completed for each student.  STS will review the request to ensure that the student is eligible for transportation and that all terms and conditions are met prior to transportation being assigned.</p>
				<p>STS will plan home to school transportation based on the student's primary address registered with the school board and to their board-designated school.  A student can only have one primary address.  The primary address must be transportation eligible as established in the STS software.</p>
				<ul>
					<li>Students who are eligible for transportation may have one (1) additional secondary address for caregiving reasons or joint custody arrangements.</li>
					<li>Secondary addresses for joint custody are complex based on the individualized nature of custodial agreements.  A separate form must be completed for Joint Custody Transportation, available at <a href="http://www.mybigyellowbus.ca" target="_blank">www.mybigyellowbus.ca</a>.</li>
				</ul>
				<div v-if="config.year == 2023">
					<p>Parents/guardians remain responsible for transportation arrangements that are beyond the scope of the service model provided by STS.</p>
				</div>
				<div v-else>
					<p>Parents/caregivers remain responsible for transportation arrangements that are beyond the scope of the service model provided by STS.</p>
				</div>
			</div>
		</div>

		<form @submit.prevent="save()">
			<fieldset>
				<legend>Student Information</legend>
				<p><em class="fas fa-info-circle"></em> All fields are mandatory. If you do not know your Student ID, please contact your school.</p>

				<div v-if="!validated">
					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="student_id">Student ID</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="student_id" v-model="student.student_id" class="form-control" required autofocus pattern="[0-9]{0,9}" maxlength="9" @keypress="noletters" @change="validated = false" />
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="dob">Date of Birth</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<nv-date v-model="student.dob" :min_date="twentyFiveYearsAgo" :max_date="new Date()" />
								</div>
							</div>
						</div>
					</div>
					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="school_id">School<br>({{config.year - 2000}}/{{config.year - 1999}} School Year)</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<select id="school_id" v-model="student.school_id" class="form-control custom-select" @change="validated = false" required>
										<option :value="null">Select School</option>
										<option v-for="s in schools" :key="s.school_id" :value="s.school_id">{{s.school_name}}</option>
									</select>
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="grade">Grade<br>({{config.year - 2000}}/{{config.year - 1999}} School Year)</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<select id="grade" v-model="student.grade" class="form-control custom-select" @change="validated = false" required>
										<option :value="null">Select Grade</option>
										<option value="jk">JK</option>
										<option value="sk">SK</option>
										<option :value="1">Grade 1</option>
										<option :value="2">Grade 2</option>
										<option :value="3">Grade 3</option>
										<option :value="4">Grade 4</option>
										<option :value="5">Grade 5</option>
										<option :value="6">Grade 6</option>
										<option :value="7">Grade 7</option>
										<option :value="8">Grade 8</option>
										<option :value="9">Grade 9</option>
										<option :value="10">Grade 10</option>
										<option :value="11">Grade 11</option>
										<option :value="12">Grade 12</option>
									</select>
								</div>
							</div>
						</div>
					</div>

					<div class="form-row mb-2">
						<div class="col-12 text-center">
							<button type="submit" class="btn btn-primary"><em class="fas fa-search loading"></em> Lookup Child Information</button>
						</div>
					</div>
				</div>

				<div v-if="validated">
					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Student ID</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.student_id}}
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Date of Birth</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.dob + "T09:00:00" | longDate}}
								</div>
							</div>
						</div>
					</div>
					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>School</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{schoolName(student.school_id)}}
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Grade</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.grade}}
								</div>
							</div>
						</div>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>School Board</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.district_id}}
								</div>
							</div>
						</div>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>First Name</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.fname}}
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Last Name</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.lname}}
								</div>
							</div>
						</div>
					</div>
					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Street Number</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.street_number}}
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Apartment</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.apartment}}
								</div>
							</div>
						</div>

						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Street Name</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.street_name}}
								</div>
							</div>
						</div>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>City</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.city}}
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label>Postal Code</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									{{student.postalcode}}
								</div>
							</div>
						</div>
					</div>
				</div>
			</fieldset>

			<fieldset v-if="validated">
				<legend>AM Transportation Requirements</legend>

				<div class="form-row mb-2">
					<div class="col-12">
						<label for="am_trans">Do you need transportation in the morning?</label>
					</div>
					<div class="col-12 col-lg-4"></div>
					<div class="col-12 col-lg-8">
						<div class="custom-control custom-radio">
							<input type="radio" id="am_trans1" name="am_trans" class="custom-control-input" v-model="student.am_trans" value="primary" required>
							<label class="custom-control-label" for="am_trans1">Yes, based on the address listed on your student file.</label>
						</div>
						<div class="custom-control custom-radio">
							<input type="radio" id="am_trans_alt" name="am_trans" class="custom-control-input" v-model="student.am_trans" value="secondary" required>
							<label class="custom-control-label" for="am_trans_alt">Yes, I need a secondary caregiver address, or we are moving to a new address.</label>
						</div>
						<div class="ml-5" v-if="student.am_trans == 'secondary'">
							<div class="custom-control custom-radio">
								<input type="radio" id="am_trans2" name="am_trans2" class="custom-control-input" v-model="student.am_trans2" value="alt" required>
								<label class="custom-control-label" for="am_trans2">Based on a secondary caregiver address.</label>
							</div>
							<div class="custom-control custom-radio">
								<input type="radio" id="am_trans3" name="am_trans2" class="custom-control-input" v-model="student.am_trans2" value="new" required>
								<label class="custom-control-label" for="am_trans3">Based on an address we are moving to.</label>
							</div>
						</div>
						<div class="custom-control custom-radio">
							<input type="radio" id="am_trans4" name="am_trans" class="custom-control-input" v-model="student.am_trans" value="no" required>
							<label class="custom-control-label" for="am_trans4">No, transportation is not needed in the morning.</label>
						</div>
					</div>
				</div>

				<fieldset v-if="student.am_trans == 'secondary' && (student.am_trans2 == 'alt' || student.am_trans2 == 'new')">
					<legend v-show="student.am_trans2 == 'alt'">Secondary Caregiver Address Information</legend>
					<legend v-show="student.am_trans2 == 'new'">New Address Information</legend>

					<div v-show="student.am_trans2 == 'alt'">
						<ul>
							<li>Service to and from a <b>secondary address</b> is an exception. The secondary address must be eligible for transportation to the school and no new bus stops or service will be added.</li>
							<li>When the secondary address is used for caregiving purposes, both the primary address and secondary address may be used on the same day, but on a consistent schedule for the entire week.  For example, the student may be picked up each morning at the secondary address bus stop and dropped of at the end of the day at the primary address bus stop.</li>
						</ul>
					</div>
					<div v-show="student.am_trans2 == 'new'">
						<ul>
							<li>Changes to the primary address must be done through the school prior to transportation being assigned or changed for the student.</li>
							<li>STS will process your registration form when we receive your new address information electronically from the school. Your new address must meet STS' eligibility requirements.</li>
						</ul>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="am_alt_street_number">Street Number</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="am_alt_street_number" v-model="student.am_alt_street_number" class="form-control" required />
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="am_alt_apartment">Apartment</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="am_alt_apartment" v-model="student.am_alt_apartment" class="form-control" />
								</div>
							</div>
						</div>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="am_alt_street_name">Street Name</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="am_alt_street_name" v-model="student.am_alt_street_name" class="form-control" required />
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="am_alt_city">City</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="am_alt_city" v-model="student.am_alt_city" class="form-control" required />
								</div>
							</div>
						</div>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="am_alt_postalcode">Postal Code</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="am_alt_postalcode" v-model="student.am_alt_postalcode" class="form-control" pattern="[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]\d[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]\s?\d[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]\d" maxlength="7" required />
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="am_alt_start">Requested Start Date</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<select v-model="student.am_alt_start" class="form-control custom-select" required>
										<option :value="null">Select a Date</option>
										<option v-for="monday in config.start_dates" :key="monday" :value="monday">
											<span v-if="monday == config.first_day">First Day of School</span>
											<span v-if="monday != config.first_day">{{monday + "T09:00:00" | longDate2}}</span>
										</option>
									</select>
								</div>
							</div>
						</div>
					</div>
				</fieldset>
			</fieldset>

			<fieldset v-if="validated">
				<legend>PM Transportation Requirements</legend>

				<div class="form-row mb-2">
					<div class="col-12">
						<label for="pm_trans">Do you need transportation in the afternoon?</label>
					</div>
					<div class="col-12 col-lg-4"></div>
					<div class="col-12 col-lg-8">
						<div class="custom-control custom-checkbox">
							<input type="checkbox" id="ampm_same" name="ampm_same" class="custom-control-input" v-model="student.ampm_same" @click="linkAMPM">
							<label class="custom-control-label" for="ampm_same">My PM requirements are the same as in the morning.</label>
						</div>
						<div v-show="!student.ampm_same">
							<div class="custom-control custom-radio">
								<input type="radio" id="pm_trans1" name="pm_trans" class="custom-control-input" v-model="student.pm_trans" value="primary" required>
								<label class="custom-control-label" for="pm_trans1">Yes, based on the address listed on your student file.</label>
							</div>
							<div class="custom-control custom-radio">
								<input type="radio" id="pm_trans_alt" name="pm_trans" class="custom-control-input" v-model="student.pm_trans" value="secondary" required>
								<label class="custom-control-label" for="pm_trans_alt">Yes, I need a secondary caregiver address, or we are moving to a new address.</label>
							</div>
							<div class="ml-5" v-if="student.pm_trans == 'secondary'">
								<div class="custom-control custom-radio">
									<input type="radio" id="pm_trans2" name="pm_trans2" class="custom-control-input" v-model="student.pm_trans2" value="alt" required>
									<label class="custom-control-label" for="pm_trans2">Based on a secondary caregiver address.</label>
								</div>
								<div class="custom-control custom-radio">
									<input type="radio" id="pm_trans3" name="pm_trans2" class="custom-control-input" v-model="student.pm_trans2" value="new" required>
									<label class="custom-control-label" for="pm_trans3">Based on an address we are moving to.</label>
								</div>
							</div>
							<div class="custom-control custom-radio">
								<input type="radio" id="pm_trans4" name="pm_trans" class="custom-control-input" v-model="student.pm_trans" value="no" required>
								<label class="custom-control-label" for="pm_trans4">No, transportation is not needed in the afternoon.</label>
							</div>
						</div>
					</div>
				</div>

				<fieldset v-if="!student.ampm_same && student.pm_trans == 'secondary' && (student.pm_trans2 == 'alt' || student.pm_trans2 == 'new')">
					<legend v-show="student.pm_trans2 == 'alt'">Secondary Caregiver Address Information</legend>
					<legend v-show="student.pm_trans2 == 'new'">New Address Information</legend>

					<div v-show="student.pm_trans2 == 'alt'">
						<ul>
							<li>Service to and from a <b>secondary address</b> is an exception. The secondary address must be eligible for transportation to the school and no new bus stops or service will be added.</li>
							<li>When the secondary address is used for caregiving purposes, both the primary address and secondary address may be used on the same day, but on a consistent schedule for the entire week.  For example, the student may be picked up each morning at the secondary address bus stop and dropped of at the end of the day at the primary address bus stop.</li>
						</ul>
					</div>
					<div v-show="student.pm_trans2 == 'new'">
						<ul>
							<li>Changes to the primary address must be done through the school prior to transportation being assigned or changed for the student.</li>
							<li>STS will process your registration form when we receive your new address information electronically from the school. Your new address must meet STS' eligibility requirements.</li>
						</ul>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="pm_alt_street_number">Street Number</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="pm_alt_street_number" v-model="student.pm_alt_street_number" class="form-control" required />
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="pm_alt_apartment">Apartment</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="pm_alt_apartment" v-model="student.pm_alt_apartment" class="form-control" />
								</div>
							</div>
						</div>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="pm_alt_street_name">Street Name</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="pm_alt_street_name" v-model="student.pm_alt_street_name" class="form-control" required />
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="pm_alt_city">City</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="pm_alt_city" v-model="student.pm_alt_city" class="form-control" required />
								</div>
							</div>
						</div>
					</div>

					<div class="form-row">
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="pm_alt_postalcode">Postal Code</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<input type="text" id="pm_alt_postalcode" v-model="student.pm_alt_postalcode" class="form-control" pattern="[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]\d[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]\s?\d[ABCEGHJKLMNPRSTVWXYZabceghjklmnprstvwxyz]\d" maxlength="7" required />
								</div>
							</div>
						</div>
						<div class="col-12 col-lg-6">
							<div class="form-row mb-2">
								<div class="col-12 col-md-6 col-lg-4">
									<label for="pm_alt_start">Requested Start Date</label>
								</div>
								<div class="col-12 col-md-6 col-lg-8">
									<select v-model="student.pm_alt_start" class="form-control custom-select" required>
										<option :value="null">Select a Date</option>
										<option v-for="monday in config.start_dates" :key="monday" :value="monday">
											<span v-if="monday == config.first_day">First Day of School</span>
											<span v-if="monday != config.first_day">{{monday + "T09:00:00" | longDate2}}</span>
										</option>
									</select>
								</div>
							</div>
						</div>
					</div>
				</fieldset>
			</fieldset>

			<fieldset v-if="validated" id="medical">
				<legend>Prevalent Medical Conditions</legend>

				<p>The Ministry of Education (PPM 161 - Supporting Students with Prevalent Medical Conditions) has identified the four (4) following Student Prevalent Medical Conditions.</p>

				<div class="form-row mb-2">
					<div class="col-12">
						<label>Select all that apply</label>
					</div>
					<div class="col-12 col-lg-4"></div>
					<div class="col-12 col-lg-8">
						<div class="custom-control custom-checkbox">
							<input type="checkbox" class="custom-control-input" id="anaphylaxis" v-model="student.anaphylaxis" @click="student.medical_na = 0">
							<label class="custom-control-label" for="anaphylaxis">Anaphylaxis</label>
						</div>
						<div class="custom-control custom-checkbox">
							<input type="checkbox" class="custom-control-input" id="asthma" v-model="student.asthma" @click="student.medical_na = 0">
							<label class="custom-control-label" for="asthma">Asthma</label>
						</div>
						<div class="custom-control custom-checkbox">
							<input type="checkbox" class="custom-control-input" id="diabetes" v-model="student.diabetes" @click="student.medical_na = 0">
							<label class="custom-control-label" for="diabetes">Diabetes</label>
						</div>
						<div class="custom-control custom-checkbox">
							<input type="checkbox" class="custom-control-input" id="epilepsy" v-model="student.epilepsy" @click="student.medical_na = 0">
							<label class="custom-control-label" for="epilepsy">Epilepsy</label>
						</div>
						<div class="custom-control custom-checkbox">
							<input type="checkbox" class="custom-control-input" id="medical_na" v-model="student.medical_na" :disabled="student.anaphylaxis + student.asthma + student.diabetes + student.epilepsy > 0">
							<label class="custom-control-label" for="medical_na">None of the above</label>
						</div>
					</div>
				</div>

				<div v-if="student.anaphylaxis == 1 || student.asthma == 1 || student.diabetes == 1 || student.epilepsy == 1">
					<p><b>*IMPORTANT NOTE*</b></p>
					<ul>
						<li>In the event of a health emergency while in transit, the bus driver will contact Dispatch to request 911 and emergency services will be dispatched to the vehicle.</li>
						<li>You have selected an identified prevalent medical condition.  Please contact your school to complete an IPOC (Individual Plan of Care form) which will be shared with STS and the Bus Operator.</li>
					</ul>

					<div class="form-row mb-2">
						<div class="col-12">
							<label>Types(s) of prescription medication used for the identified Prevalent Medical Conditions and carried with student in the event of a health emergency:</label>
						</div>
						<div class="col-12 col-lg-4"></div>
						<div class="col-12 col-lg-8">
							<div class="custom-control custom-checkbox">
								<input type="checkbox" class="custom-control-input" id="epipen" v-model="student.epipen">
								<label class="custom-control-label" for="epipen">Epi Pen (or similar)</label>
							</div>
							<div class="custom-control custom-checkbox">
								<input type="checkbox" class="custom-control-input" id="glucagon" v-model="student.glucagon">
								<label class="custom-control-label" for="glucagon">Glucagon Spray</label>
							</div>
							<div class="custom-control custom-checkbox">
								<input type="checkbox" class="custom-control-input" id="inhaler" v-model="student.inhaler">
								<label class="custom-control-label" for="inhaler">Rescue Inhaler</label>
							</div>
						</div>
					</div>
					<div class="form-row mb-2">
						<div class="col-12">
							<label>Student carries prescription medication on their person to self-administer during a health emergency and it is located in:</label>
						</div>
						<div class="col-12 col-lg-4"></div>
						<div class="col-12 col-lg-8">
							<div class="custom-control custom-checkbox">
								<input type="checkbox" class="custom-control-input" id="bpfront" v-model="student.bpfront">
								<label class="custom-control-label" for="bpfront">Back pack - front pocket</label>
							</div>
							<div class="custom-control custom-checkbox">
								<input type="checkbox" class="custom-control-input" id="bpside" v-model="student.bpside">
								<label class="custom-control-label" for="bpside">Back pack - side pocket</label>
							</div>
							<div class="custom-control custom-checkbox">
								<input type="checkbox" class="custom-control-input" id="bpmain" v-model="student.bpmain">
								<label class="custom-control-label" for="bpmain">Back pack - main body</label>
							</div>
							<div class="custom-control custom-checkbox">
								<input type="checkbox" class="custom-control-input" id="bpwaist" v-model="student.bpwaist">
								<label class="custom-control-label" for="bpwaist">Waist pack</label>
							</div>
						</div>
					</div>
				</div>
			</fieldset>

			<fieldset v-if="validated">
				<legend>Terms and Conditions</legend>

				<div v-if="config.year == 2023">
					<ul>
						<li>Normal processing time is 7-10 business days. During late August and the month of September this timeline may be extended.</li>
						<li>Any registration submitted prior to Thursday at Noon will be processed for the following week effective Monday.</li>
						<li>Any registrations submitted after Thursday at Noon will be processed for the next available Monday start date</li>
						<li><b>Parents/Guardians should be prepared to make alternate transportation arrangements until your forms are processed.</b></li>
						<li><b>Once your registration is processed, all busing information such as stop locations, pickup and drop off times, and bus # will be available in the Parent Portal at www.mybigyellowbus.ca.</b></li>
						<li>Forms that do not meet STS’ terms and conditions will be rejected.</li>
						<li>STS will verify the information of the Parent/Guardian submitting the application form. If the Parent/Guardian information cannot be matched to the school records the application form will not be processed.</li>
					</ul>

					<p>Parent/Guardian Electronic Signature: By printing your name below, you acknowledge that: You are authorized to provide the personal and medical information of the named student. You understand the information provided will be shared with the bus company and school board for the purpose of organizing transportation for the named student.</p>
				</div>
				<div v-else>
					<ul>
						<li>Your registration will be processed over the summer months.  Transportation details including your Bus Run #, Stop Location, Pickup and Drop-Off times and Bus Company will be available in the STS Parent Portal in Late August.</li>
						<li>STS strongly recommends Caregivers log into the STS Parent Portal in Late August to review their transportation arrangements, transportation arrangements may change from year to year.</li>
						<li><b>Parents/Caregivers should be prepared to make alternate transportation arrangements until your registration is processed.</b></li>
						<li>Registrations that do not meet STS’ Policies, Terms and Conditions will be rejected.</li>
					</ul>

					<p>Parent/Caregiver Electronic Signature: By printing your name below, you acknowledge that: You are authorized to provide the personal and medical information of the named student. You understand the information provided will be shared with the bus company and school board for the purpose of organizing transportation for the named student.</p>
				</div>

				<div class="form-row">
					<div class="col-12 col-lg-6">
						<div class="form-row mb-2">
							<div class="col-12 col-md-6 col-lg-4">
								<label for="applicant_fname">First Name</label>
							</div>
							<div class="col-12 col-md-6 col-lg-8">
								<input type="text" id="applicant_fname" v-model="student.applicant_fname" class="form-control" required />
							</div>
						</div>
					</div>
					<div class="col-12 col-lg-6">
						<div class="form-row mb-2">
							<div class="col-12 col-md-6 col-lg-4">
								<label for="applicant_lname">Last Name</label>
							</div>
							<div class="col-12 col-md-6 col-lg-8">
								<input type="text" id="applicant_lname" v-model="student.applicant_lname" class="form-control" required />
							</div>
						</div>
					</div>
				</div>

				<div class="form-row">
					<div class="col-12 col-lg-6">
						<div class="form-row mb-2">
							<div class="col-12 col-md-6 col-lg-4">
								<label for="applicant_phone">Phone Number</label>
							</div>
							<div class="col-12 col-md-6 col-lg-8">
								<input type="tel" id="applicant_phone" v-model="student.applicant_phone" class="form-control" maxlength="19" v-on:keyup="phoneFormat(student, 'applicant_phone')" required />
							</div>
						</div>
					</div>
					<div class="col-12 col-lg-6">
						<div class="form-row mb-2">
							<div class="col-12 col-md-6 col-lg-4">
								<label for="applicant_email">Email Address</label>
							</div>
							<div class="col-12 col-md-6 col-lg-8">
								<input type="email" id="applicant_email" v-model="student.applicant_email" class="form-control" required />
							</div>
						</div>
					</div>
				</div>

				<div class="form-row mb-2">
					<div class="col-12 col-lg-4"></div>
					<div class="col-12 col-lg-8">
						<div class="custom-control custom-checkbox">
							<input type="checkbox" id="tos" name="tos" class="custom-control-input" v-model="student.tos" required>
							<label class="custom-control-label" for="tos">I agree to the above terms and conditions</label>
						</div>
					</div>
				</div>
			</fieldset>

			<div class="form-row mb-2" v-if="validated">
				<div class="col-12 text-center">
					<button type="submit" class="btn btn-primary"><em class="fas fa-save loading"></em> Submit Information</button>
				</div>

				<p style="font-size: 0.6em;">Notice of Collection:</p>
				<p style="font-size: 0.6em;">Southwestern Ontario Student Transportation Services ("STS") acts on behalf of the London District Catholic School Board and the Thames Valley District School Board and contracts transportation service providers to arrange transportation to and from school for eligible students. The personal information you provide on this form will be shared with the relevant staff of STS, school board, and transportation provider for the purpose of providing transportation. The information collected is treated as described in our privacy policy and in accordance with applicable laws.</p>
			</div>
		</form>
	</div>
</template>

<script>
import nvDate from "@/components/nvDate";

export default
{
	name: 'Registration',
	components: {
		nvDate
	},
	data: function()
	{
		return {
			student: {
				student_id: null,
				dob: null,
				school_id: null,
				grade: null,
				fname: null,
				lname: null,
				district_id: null,
				street_number: null,
				street_name: null,
				apartment: null,
				city: null,
				postalcode: null,
				am_alt_start: null,
				am_alt_street_number: null,
				am_alt_street_name: null,
				am_alt_apartment: null,
				am_alt_city: null,
				am_alt_postalcode: null,
				pm_alt_start: null,
				pm_alt_street_number: null,
				pm_alt_street_name: null,
				pm_alt_apartment: null,
				pm_alt_city: null,
				pm_alt_postalcode: null,
				anaphylaxis: false,
				asthma: false,
				diabetes: false,
				epilepsy: false,
				medical_na: false,
				ampm_same: false,
				am_trans: null,
				am_trans2: null,
				am_trans_alt: 0,
				pm_trans: null,
				pm_trans2: null,
				pm_trans_alt: 0,
				applicant_fname: null,
				applicant_lname: null,
				applicant_phone: null,
				applicant_email: null,
				applicant_tos: false,
				epipen: false,
				glucagon: false,
				inhaler: false,
				bpfront: false,
				bpside: false,
				bpmain: false,
				bpwaist: false,
			},
			validated: false,
			schools: [],
			config: {
				year: 2023,
				start_dates: []
			}
		}
	},
	mounted: function()
	{
		this.config.year = this.NSTS_CONFIG.year;
		this.loadConfig();
		this.loadSchools();
	},
	computed:
	{
		mondays: function()
		{
			var mondays = [];
			var d = new Date();
			d.setHours(9);

			// Find the first available Monday from today onward.
			while(d.getDay() !== 1)
			{
				d.setDate(d.getDate() + 1);
			}

			// Find lots of Mondays.
			for(var i=0; i<100; i++)
			{
				var date = d.toISOString().substring(0, 10);
				var date_string = date.substring(0, 4) + "-" + date.substring(5, 7) + "-" + date.substring(8, 10);

				mondays.push({"date": date_string});

				// Stop at June 30, {school year + 1}
				if(date_string > (this.config.year + 1) + "-06-30")
				{
					break;
				}

				d.setDate(d.getDate() + 7);
			}

			return mondays;
		},
		twentyFiveYearsAgo: function()
		{
			var d = new Date();
			d.setYear(d.getFullYear() - 25);

			return d;
		}
	},
	methods:
	{
		save: function()
		{
			if(this.validated)
			{
				this._save();
			}
			else
			{
				this.checkID();
			}
		},
		_save: function()
		{
			var vm = this;

			if(vm.student.anaphylaxis + vm.student.asthma + vm.student.diabetes + vm.student.epilepsy + vm.student.medical_na == 0)
			{
				vm.showError("Data Error", "You did not select an option for 'Prevalent Medical Conditions'.", true, function()
				{
					document.getElementById("medical").scrollIntoView();
				});
				return;
			}

			// VERY basic email validation, due to the fact that browsers would allow bob@somewhere without a TLD.
			if(!vm.student.applicant_email.match(/.+?@.+?\..+/))
			{
				vm.showError("Invalid Email Address", "Your email address appears to be invalid.", true, function()
				{
					document.getElementById("applicant_email").scrollIntoView();
				});
				return;
			}

			vm.loading = true;
			vm.startLoading();

			vm.CORS("POST", "/registration/save", JSON.stringify(vm.student),
			function()
			{
				if(vm.NSTS_CONFIG.current_year)
				{
					vm.showInfo("Student Registration Submitted", "<p>Thank you for registering for the school bus.</p><ul class='text-left'><li>Normal processing time is 7-10 business days. During late August and the month of September this timeline may be extended.</li><li>Any registration submitted prior to Thursday at Noon will be processed for the following week effective Monday.</li><li>Any registrations submitted after Thursday at Noon will be processed for the next available Monday start date</li><li>Parents/Guardians should be prepared to make alternate transportation arrangements until your forms are processed.</li><li>Once your registration is processed, all busing information such as stop locations, pickup and drop off times, and bus # will be available in the Parent Portal at <a href='https://www.mybigyellowbus.ca'>www.mybigyellowbus.ca</a>.</li></ul>", true, function()
					{
						location.reload();
					});
				}
				else
				{
					vm.showInfo("Student Registration Submitted", "<p>Thank you for registering for the school bus.</p><p class='text-left'>" + vm.NSTS_CONFIG.year + "-" + (vm.NSTS_CONFIG.year + 1) + " transportation information will be available in the Parent portal, <a href='http://www.mybigyellowbus.ca' target='_blank'>www.mybigyellowbus.ca</a> in <b>LATE AUGUST</b>. Please check your Parent Portal in Late August for you transportation information including bus stop location and pickup and drop off times.</p>", true, function()
					{
						location.reload();
					});
				}

				vm.loading = false;
				vm.stopLoading();
			}, function(response)
			{
				vm.showError("Invalid Student Information", response.responseText, true, null);
				vm.loading = false;
				vm.stopLoading();
			});
		},
		checkID: function()
		{
			var vm = this;
			vm.loading = true;
			vm.validated = false;
			vm.startLoading();

			vm.CORS("POST", "/registration/checkID", JSON.stringify(vm.student),
			function(response)
			{
				vm.student.fname = response.FirstName;
				vm.student.lname = response.LastName;
				vm.student.district_id = response.DistrictID;

				if(response.addresses.length > 0)
				{
					vm.student.street_number = response.addresses[0].StreetNumber;
					vm.student.street_name = response.addresses[0].StreetName;
					vm.student.apartment = response.addresses[0].Apartment;
					vm.student.city = response.addresses[0].Municipality;
					vm.student.postalcode = response.addresses[0].PostalCode;
				}

				if(response.Contacts.length > 0)
				{
					vm.student.parent_fname = response.Contacts[0].FirstName;
					vm.student.parent_lname = response.Contacts[0].LastName;
					vm.student.parent_email = response.Contacts[0].Email1;
					vm.student.parent_phone = response.Contacts[0].HomePhone;
					vm.student.parent_cell = response.Contacts[0].CellPhone;
					vm.student.parent_work = response.Contacts[0].WorkPhone;
				}

				vm.validated = true;
				vm.stopLoading();
			}, function(response)
			{
				if(response.responseCode == 409)
				{
					vm.showError("Student Not Eligible", response.responseText, true, null);
				}
				else
				{
					vm.showError("Invalid Student Information", response.responseText, true, null);
				}

				vm.loading = false;
				vm.stopLoading();
			});
		},
		loadConfig: function()
		{
			var vm = this;
			vm.startLoading();

			vm.CORS('GET', "/registration/configuration", null,
			function(data)
			{
				data["year"] = Number(data["year"]);
				data["start_dates"] = JSON.parse(data["start_dates"]);

				vm.config = data;
				vm.stopLoading();
			}, function(response)
			{
				vm.stopLoading();
				vm.showError("Error", "Error loading configuration: " + response.responseText, true, null);
			});
		},
		loadSchools: function()
		{
			var vm = this;
			vm.startLoading();

			vm.CORS('GET', "/registration/schools", null,
			function(data)
			{
				vm.schools = data;
				vm.stopLoading();
			}, function(response)
			{
				vm.stopLoading();
				vm.showError("Error", "Error loading user schools: " + response.responseText, true, null);
			});
		},
		schoolName: function(school_id)
		{
			for(var i=0; i<this.schools.length; i++)
			{
				if(this.schools[i].school_id == school_id)
				{
					return this.schools[i].school_name;
				}
			}

			return school_id;
		},
		noletters: function(e)
		{
			if(e.keyCode < 48 || e.keyCode > 57)
			{
				e.preventDefault();
				return false;
			}
		},
		linkAMPM: function()
		{
			var vm = this;

			setTimeout(function()
			{
				if(vm.student.ampm_same)
				{
					vm.student.pm_trans = vm.student.am_trans;
					vm.student.pm_trans2 = vm.student.am_trans2;
					vm.student.pm_trans_alt = vm.student.am_trans_alt;
					vm.student.pm_alt_start = vm.student.am_alt_start;
					vm.student.pm_alt_street_number = vm.student.am_alt_street_number;
					vm.student.pm_alt_street_name = vm.student.am_alt_street_name;
					vm.student.pm_alt_apartment = vm.student.am_alt_apartment;
					vm.student.pm_alt_city = vm.student.am_alt_city;
					vm.student.pm_alt_postalcode = vm.student.am_alt_postalcode;
				}
			}, 1000);
		}
	}
}
</script>
